/**
 * RULES (EXPERT-NETWORKS.md §12) — an Expert Network's RULEBOOK at network level: Teams → Responsibilities → Rules,
 * with a lead + agents per responsibility. It is the BASE every room of this network starts from; rooms adjust their
 * copy and may propose changes back (accepted here). Ships with the next release.
 *
 * PLANNER-ONLY (owner 2026-10-21): nothing here is edited by hand — an isolated edit leaves the rest of the network
 * unaware. A person SELECTS teams / responsibilities / rules (click; the selection PREFILLS the prompt with each item's
 * id + text, like the room's Tasks-tab selection), adds what they want, and "Draft changes" asks the planner, which
 * adjusts the whole rulebook. The draft is confirmed on the banner above the prompt box (Apply / Discard); what the
 * network itself must gain (a new agent) goes to Build. A room's suggestion is ACCEPTED the same way: Accept selects it
 * into the prompt, the draft leads with the room's change as made, Apply accepts it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import { getExpertRules, setExpertRules } from '@stepflow/ui-shared/services/dataPlaneApi';
import CommandDeck, { DECK_ICON_BUTTON } from '@stepflow/ui-shared/components/CommandDeck.jsx';
import DeckLeftGroup from '@stepflow/ui-shared/components/DeckLeftGroup.jsx';
import { useMarkdownField } from '@stepflow/ui-shared/hooks/useMarkdownField.js';
import ExpertTrainingView from './ExpertTrainingView.jsx';
import ExpertSubscriptionFormView from './ExpertSubscriptionFormView.jsx';
import {
  getWorkbenchPromptInputClassName,
  WORKBENCH_PROMPT_FIELD_STYLE,
} from '@stepflow/ui-shared/utils/workbenchPromptFieldClassNames.js';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const SEV = { block: 'bg-rose-50 text-rose-700', warn: 'bg-amber-50 text-amber-800', advise: 'bg-gray-100 text-gray-600' };
const errText = (e) => (e && e.data && e.data.message) || (e && e.message) || "Couldn't do that.";
const KIND = { team: 'team', resp: 'responsibility', rule: 'rule', proposal: 'suggestion' };
const selKey = (s) => `${s.kind}:${s.id}`;

/** The prompt prefill for a selection: one line per item — its kind, unique id and text. Pure. */
export function selectionPrefill(selection = []) {
  if (!selection.length) return '';
  return `${selection.map((s) => `[${KIND[s.kind]} ${s.id}] ${s.text}`.trim()).join('\n')}\n`;
}

/** Swap our previous prefill for the new one, keeping what the person typed after it; text they rewrote stays. Pure. */
export function refillPrompt(prompt, oldPrefill, newPrefill) {
  const p = String(prompt || '');
  if (!p.trim()) return newPrefill;
  if (oldPrefill && p.startsWith(oldPrefill)) return newPrefill + p.slice(oldPrefill.length);
  return p;
}

function describeOp(op) {
  const r = op.rule || {};
  switch (op.op) {
    case 'team.add': return `New team “${op.name}”`;
    case 'team.edit': return `Change team “${op.team}”${op.name && op.name !== op.team ? ` → “${op.name}”` : ''}`;
    case 'team.remove': return `Remove team “${op.team}”`;
    case 'resp.add': return `New responsibility “${op.title}” in ${op.team}${op.lead ? ` (lead ${op.lead})` : ''}`;
    case 'resp.edit': return `Change responsibility ${op.key}`;
    case 'resp.remove': return `Remove responsibility ${op.key}`;
    case 'rule.add': return `New rule: ${r.statement}${r.severity ? ` (${r.severity})` : ''}`;
    case 'rule.edit': return `Change rule ${op.id}${r.severity ? ` → ${r.severity}` : ''}${r.statement ? `: ${r.statement}` : ''}`;
    case 'rule.remove': return `Remove rule ${op.id}`;
    default: return op.op;
  }
}

function describeChange(c) {
  const r = c.rule || {};
  if (c.kind === 'procedure') return `Procedure: ${c.title || c.procedureId}`;
  if (c.kind === 'add') return `Add: ${r.statement}${r.severity ? ` (${r.severity})` : ''}`;
  if (c.kind === 'remove') return `Remove: ${c.statement || c.ruleId}`;
  return `Change: ${r.statement || c.statement || c.ruleId}${r.severity ? ` (${r.severity})` : ''}`;
}

export default function ExpertRulesTab({ network, onAskBuild }) {
  const networkId = network?.networkId;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [ask, setAsk] = useState('');
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState('');                   // the team whose tab is open
  const [selection, setSelection] = useState([]);       // [{kind: team|resp|rule, id, text}]
  const [view, setView] = useState('rules');            // rules | training (§15d — what it knows + what changed) | form (§14b)
  const lastPrefill = useRef('');

  const load = useCallback(async () => {
    if (!networkId) return;
    try {
      setData(await getExpertRules(networkId));
      setErr('');
    } catch (e) {
      setErr(errText(e));
    }
  }, [networkId]);
  useEffect(() => { load(); }, [load]);

  const book = data?.rulebook || { teams: [], version: 0 };
  const canEdit = !!data?.canEdit;
  const pending = (data?.proposals || []).filter((p) => p.status === 'pending');
  const teams = book.teams || [];
  const team = teams.find((t) => t.name === tab) || teams[0] || null;   // a renamed/removed team falls back to the first
  const ruleCount = (t) => (t.responsibilities || []).reduce((n, r) => n + (r.rules || []).length, 0);

  // the selection PREFILLS the prompt (id + text per item); what the person typed after it is kept
  const pick = (item) => {
    if (!canEdit) return;
    const next = selection.some((s) => selKey(s) === selKey(item))
      ? selection.filter((s) => selKey(s) !== selKey(item)) : [...selection, item];
    const old = lastPrefill.current;
    const pf = selectionPrefill(next);
    lastPrefill.current = pf;
    setSelection(next);
    setAsk((p) => refillPrompt(p, old, pf));
  };
  const clearSelection = () => {
    const old = lastPrefill.current;
    setAsk((p) => refillPrompt(p, old, ''));
    lastPrefill.current = '';
    setSelection([]);
  };
  const isSel = (kind, id) => selection.some((s) => s.kind === kind && s.id === id);
  const selCls = (on) => (on ? 'bg-indigo-50 ring-1 ring-indigo-300' : canEdit ? 'hover:bg-gray-50' : '');

  const act = async (key, body) => {
    setBusy(key);
    setErr('');
    try {
      const r = await setExpertRules(networkId, body);
      if (r?.rulebook) setData((d) => ({ ...(d || {}), ...r }));
      return r;
    } catch (e) {
      setErr(errText(e));
      if (e && e.status === 409) load();
      return null;
    } finally {
      setBusy('');
    }
  };
  const author = async () => {
    const r = await act('author', { action: 'author', ask,
      selected: selection.map(({ kind, id }) => ({ kind, id })) });
    if (r?.draft) setDraft(r.draft);
  };
  const applyDraft = async () => {
    const r = await act('apply', { action: 'apply', draftId: draft.draftId });
    if (r) {
      setDraft(null);
      setAsk('');
      lastPrefill.current = '';
      setSelection([]);
    } else {
      setDraft(null);                                   // a stale / applied draft is gone — the error says why
    }
  };

  if (!networkId) return null;
  const switcher = (
    <div className="flex gap-1 px-4 pt-3" role="tablist" aria-label="Rules or training" data-testid="expert-rules-view">
      {[['rules', 'Rules'], ['training', 'Training'], ['form', 'Subscription form']].map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
          data-testid={`expert-view-${id}`}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${view === id
            ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}>
          {label}
        </button>
      ))}
    </div>
  );
  if (view === 'form') {
    return (
      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="expert-rules-tab">
        {switcher}
        <div className="min-h-0 flex-1 overflow-y-auto"><ExpertSubscriptionFormView network={network} canEdit={canEdit} /></div>
      </div>
    );
  }
  if (view === 'training') {
    return (
      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="expert-rules-tab">
        {switcher}
        <div className="min-h-0 flex-1 overflow-y-auto"><ExpertTrainingView network={network} /></div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white text-[12px]" data-testid="expert-rules-tab">
      {switcher}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Rules</h3>
            <p className="mt-0.5 max-w-prose text-xs text-gray-500">
              This network's teams, what each is responsible for, and the rules every piece of their work is checked
              against. Every room of this network starts from these; rooms adjust their own copy and can suggest
              changes back. Changes take effect on the next release.
              {canEdit ? ' To change anything, select it and ask the planner below.' : ''}
            </p>
          </div>
          <span className="shrink-0 text-[11px] text-gray-400" data-testid="expert-rules-version">
            {book.version ? `v${book.version}${book.by ? ` · ${book.by}` : ''}` : 'No rules yet'}
          </span>
        </div>

        {pending.length ? (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-2" data-testid="expert-rules-proposals">
            <p className="text-[11px] font-medium text-amber-900">Suggested by rooms ({pending.length})</p>
            {pending.map((p) => (
              <div key={p.proposalId} className="mt-1.5 flex items-start gap-2" data-testid={`expert-proposal-${p.proposalId}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-gray-800">{describeChange(p.change)}</p>
                  <p className="text-[11px] text-gray-500">{p.team ? `${p.team} · ` : ''}{p.room} · {p.by}{p.reason ? ` — ${p.reason}` : ''}</p>
                  {p.change?.kind === 'procedure' && p.change.text ? (
                    <details className="mt-0.5"><summary className="cursor-pointer text-[10px] text-amber-800">Read the procedure</summary>
                      <pre className="mt-0.5 whitespace-pre-wrap text-[10px] text-gray-700" data-testid={`expert-proposal-text-${p.proposalId}`}>{p.change.text}</pre>
                    </details>
                  ) : null}
                </div>
                {canEdit && p.change?.kind === 'procedure' ? (
                  // a PROCEDURE is a distilled, reviewed, reusable process (EXPERT-ROOMS.md §13a) — a standalone
                  // document the builders add directly (it ships with the next release); no rule structure to adjust
                  <span className="flex shrink-0 gap-1">
                    <button type="button" disabled={!!busy} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}
                      data-testid={`expert-proposal-add-${p.proposalId}`}
                      onClick={() => act(`p:${p.proposalId}`, { action: 'proposal', proposalId: p.proposalId, decision: 'accept' })}>
                      Add procedure</button>
                    <button type="button" disabled={!!busy} className={`${btn} text-amber-800 hover:bg-amber-100`}
                      onClick={() => act(`p:${p.proposalId}`, { action: 'proposal', proposalId: p.proposalId, decision: 'decline' })}>
                      Decline</button>
                  </span>
                ) : canEdit ? (
                  <span className="flex shrink-0 gap-1">
                    {/* accepting changes the rules → through the planner: it prefills the prompt; Draft changes then
                        leads with the room's change as made and adjusts the rest; Apply on the banner accepts it */}
                    <button type="button" disabled={!!busy} aria-pressed={isSel('proposal', p.proposalId)}
                      className={`${btn} ${isSel('proposal', p.proposalId)
                        ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-400' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                      data-testid={`expert-proposal-accept-${p.proposalId}`} title="Accept it through the planner"
                      onClick={() => pick({ kind: 'proposal', id: p.proposalId,
                        text: `${describeChange(p.change)} — from ${p.room}${p.reason ? ` (${p.reason})` : ''}` })}>
                      {isSel('proposal', p.proposalId) ? 'Selected' : 'Accept'}</button>
                    <button type="button" disabled={!!busy} className={`${btn} text-amber-800 hover:bg-amber-100`}
                      data-testid={`expert-proposal-decline-${p.proposalId}`}
                      onClick={() => act(`p:${p.proposalId}`, { action: 'proposal', proposalId: p.proposalId, decision: 'decline' })}>
                      Decline</button>
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {err ? <p className="mt-2 text-[11px] text-rose-600" data-testid="expert-rules-error">{err}</p> : null}

        {/* one TAB per team (not a serial list) */}
        {teams.length ? (
          <div className="mt-3 flex flex-wrap items-end gap-1 border-b border-gray-200" role="tablist" data-testid="expert-team-tabs">
            {teams.map((t) => {
              const on = team && t.name === team.name;
              return (
                <button key={t.name} type="button" role="tab" aria-selected={on} data-testid={`expert-team-tab-${t.name}`}
                  onClick={() => setTab(t.name)}
                  className={`-mb-px rounded-t-md border px-2.5 py-1 text-xs font-medium transition-colors ${on
                    ? 'border-gray-200 border-b-white bg-white text-gray-900'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}>
                  {t.name}<span className="ml-1 text-[10px] text-gray-400">{ruleCount(t)}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="mt-3 space-y-3">
          {(team ? [team] : []).map((t) => (
            <section key={t.name} role="tabpanel" className="rounded-lg border border-gray-200" data-testid={`expert-team-${t.name}`}>
              <header
                className={`cursor-${canEdit ? 'pointer' : 'default'} rounded-t-lg border-b border-gray-100 px-3 py-2 ${selCls(isSel('team', t.name))}`}
                data-testid={`expert-select-team-${t.name}`} aria-pressed={isSel('team', t.name)}
                onClick={() => pick({ kind: 'team', id: t.name, text: t.criterion || '' })}>
                <p className="text-[13px] font-semibold text-gray-900">{t.name}</p>
                {t.criterion ? <p className="text-[11px] text-gray-500">{t.criterion}</p> : null}
              </header>
              <div className="space-y-2 px-3 py-2">
                {(t.responsibilities || []).map((r) => (
                  <div key={r.key} data-testid={`expert-resp-${r.key}`}>
                    <div
                      className={`flex cursor-${canEdit ? 'pointer' : 'default'} flex-wrap items-center gap-2 rounded px-1 py-0.5 ${selCls(isSel('resp', r.key))}`}
                      data-testid={`expert-select-resp-${r.key}`} aria-pressed={isSel('resp', r.key)}
                      onClick={() => pick({ kind: 'resp', id: r.key, text: r.title || '' })}>
                      <p className="text-[12px] font-medium text-gray-800">{r.title}</p>
                      <span className="text-[11px] text-gray-500">{r.lead ? `lead ${r.lead}` : 'no lead'}</span>
                      {(r.agents || []).map((a) => (
                        <span key={a} className="rounded bg-gray-100 px-1 py-px text-[10px] text-gray-600">{a}</span>
                      ))}
                    </div>
                    <ul className="mt-1 space-y-1 border-l border-gray-100 pl-3">
                      {(r.rules || []).map((rule) => (
                        <li key={rule.id} data-testid={`expert-rule-${rule.id}`}
                          className={`flex cursor-${canEdit ? 'pointer' : 'default'} items-start gap-2 rounded px-1 py-0.5 ${selCls(isSel('rule', rule.id))}`}
                          aria-pressed={isSel('rule', rule.id)}
                          onClick={() => pick({ kind: 'rule', id: rule.id, text: rule.statement || '' })}>
                          <span className={`mt-px shrink-0 rounded px-1 py-px text-[10px] ${SEV[rule.severity] || SEV.advise}`}>
                            {rule.severity}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] text-gray-800">{rule.statement}</p>
                            {rule.check?.plan || rule.check?.run ? (
                              <p className="text-[11px] text-gray-500">
                                {rule.check.plan ? `Before: ${rule.check.plan}` : ''}
                                {rule.check.plan && rule.check.run ? ' · ' : ''}
                                {rule.check.run ? `After: ${rule.check.run}` : ''}</p>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {!teams.length && data ? (
            <p className="text-[11px] text-gray-400">No rules yet — ask the planner below.</p>
          ) : null}
        </div>
      </div>
      {canEdit ? (
        <PlannerComposer ask={ask} setAsk={setAsk} busy={busy} draft={draft} onDraft={author}
          selected={selection.length} onClearSelection={clearSelection}
          onApply={applyDraft} onDiscard={() => setDraft(null)}
          onAskBuild={onAskBuild ? () => onAskBuild((draft?.networkChanges || []).join('\n')) : null} />
      ) : null}
    </div>
  );
}

/** "Ask the planner" — the Build tab's three-deck prompt box (CommandDeck: upper deck · prompt · lower deck with the
 *  send button labelled "Draft changes"). The planner's draft is confirmed on the banner just above the box (the
 *  network Approve band's shape: Apply / Discard) — nothing is saved until Apply. */
function PlannerComposer({ ask, setAsk, busy, draft, onDraft, onApply, onDiscard, onAskBuild, selected,
  onClearSelection }) {
  const can = !!ask.trim() && !busy;
  const submit = (e) => { e?.preventDefault?.(); if (can) onDraft(); };
  const fieldRef = useRef(null);
  const { format, handleFormatKey } = useMarkdownField(ask, setAsk, fieldRef);
  const needs = draft?.networkChanges || [];
  const band = draft ? (
    <div className="flex w-full items-start justify-between gap-2" data-testid="expert-rules-draft">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-700">{draft.summary || 'The planner suggests:'}</p>
        <ul className="mt-1 list-disc pl-4 text-[11px] text-gray-700">
          {draft.ops.map((op, i) => <li key={i}>{describeOp(op)}</li>)}
        </ul>
        {needs.length ? (
          <div className="mt-1 text-[11px] text-amber-800" data-testid="expert-rules-draft-network">
            <span className="font-medium">The network itself needs:</span> {needs.join(' · ')}
            {onAskBuild ? (
              <button type="button" onClick={onAskBuild} data-testid="expert-rules-draft-build"
                className="ml-1.5 font-medium text-indigo-700 hover:underline">Ask Build</button>
            ) : null}
          </div>
        ) : null}
        {(draft.errors || []).length ? <p className="mt-1 text-[11px] text-gray-400">{draft.errors.join(' · ')}</p> : null}
      </div>
      <span className="flex shrink-0 items-center gap-1.5">
        <button type="button" disabled={!!busy || !draft.ops.length} onClick={onApply} data-testid="expert-rules-draft-apply"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-50">
          <Check className="h-3.5 w-3.5" aria-hidden strokeWidth={2.5} />Apply
        </button>
        <button type="button" onClick={onDiscard} data-testid="expert-rules-draft-discard"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors">
          <X className="h-3.5 w-3.5" aria-hidden strokeWidth={2.5} />Discard
        </button>
      </span>
    </div>
  ) : null;
  return (
    <div className="shrink-0 px-6 pb-2 pt-2">
      <div className="w-full">
        <form onSubmit={submit} className="relative z-20">
          <CommandDeck
            testId="expert-rules-deck"
            alertBand={band}
            topLeft={(
              // THE SHARED UPPER-LEFT GROUP (DeckLeftGroup — the same in every prompt box)
              <DeckLeftGroup testId="expert-rules" onFormat={format} formatDisabled={busy === 'author'}
                attachSoon="Attaching files to the planner — coming soon" snippetSoon="Code snippets for the planner — coming soon" />
            )}
            topRight={(
              <span className="inline-flex items-center gap-1">
                {selected ? (
                  <button type="button" className={DECK_ICON_BUTTON} onClick={onClearSelection} data-testid="expert-rules-selection">
                    {selected} selected<X className="h-3 w-3" aria-hidden />
                  </button>
                ) : null}
                <span className={DECK_ICON_BUTTON} data-testid="expert-rules-planner-label"
                  title="Select teams, responsibilities or rules above, then say what to change">
                  <Sparkles className="h-4 w-4 shrink-0" aria-hidden /><span className="whitespace-nowrap">Planner</span>
                </span>
              </span>
            )}
            bottomRight={(
              <button type="submit" disabled={!can} data-testid="expert-rules-author"
                className={`inline-flex h-7 shrink-0 items-center justify-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors ${
                  can ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-transparent text-gray-300 cursor-not-allowed'}`}>
                {busy === 'author' ? 'Drafting…' : 'Draft changes'}
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M1.5 2.1a.75.75 0 0 1 .82-.14l16 7.35a.75.75 0 0 1 0 1.37l-16 7.35a.75.75 0 0 1-1.05-.86L3.1 10 1.27 2.93a.75.75 0 0 1 .23-.83ZM4.43 10.75l-1.25 4.86L15.6 10 3.18 4.39l1.25 4.86h5.82a.75.75 0 0 1 0 1.5H4.43Z" />
                </svg>
              </button>
            )}
          >
            <div className="relative flex flex-col gap-2">
              <textarea
                ref={fieldRef}
                rows={Math.min(6, Math.max(1, ask.split('\n').length))}
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (handleFormatKey(e)) return; if (e.key === 'Enter' && !e.shiftKey) submit(e); }}
                disabled={busy === 'author'}
                placeholder="Ask the planner — e.g. add a Cost team with tagging and budget rules"
                aria-label="Ask the planner"
                data-testid="expert-rules-ask"
                className={`${getWorkbenchPromptInputClassName({ disabled: busy === 'author', size: 'deck' })} resize-none bg-transparent`}
                style={WORKBENCH_PROMPT_FIELD_STYLE}
              />
            </div>
          </CommandDeck>
        </form>
      </div>
    </div>
  );
}
